import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  deleteHotlist,
  importHotlist,
  listHotlist,
  type HotlistEntry,
} from "@/api/hotlist";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

export function HotlistPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useQuery({
    queryKey: ["hotlist", q, page],
    queryFn: () => listHotlist({ q: q || undefined, page, size: 50 }),
  });

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const formato = file.name.toLowerCase().endsWith(".csv") ? "csv" : "xlsx";
    try {
      const result = await importHotlist(formato, file);
      toast.push(
        `Importados ${result.insertados} · omitidos ${result.omitidos} · errores ${result.errores}`,
        "success"
      );
      await qc.invalidateQueries({ queryKey: ["hotlist"] });
    } catch {
      toast.push("Error al importar", "error");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div className="p-6 space-y-4" data-testid="page-hotlist">
      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Hotlist</h1>
        <input
          data-testid="hotlist-search"
          className="input ml-auto max-w-sm"
          placeholder="Buscar VIN, placa, marca…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          data-testid="hotlist-file"
          onChange={handleFile}
        />
        <button
          className="btn-primary"
          data-testid="hotlist-import"
          onClick={() => fileRef.current?.click()}
        >
          Importar
        </button>
      </header>

      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">VIN</th>
              <th>Placa</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Estado</th>
              <th>Reportado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((row: HotlistEntry) => (
              <tr key={row.id} className="border-b border-border/40" data-testid={`hotlist-row-${row.id}`}>
                <td className="py-2 font-mono text-xs">{row.vin ?? ""}</td>
                <td className="font-mono text-xs">{row.placa ?? ""}</td>
                <td>{row.marca ?? ""}</td>
                <td>{row.modelo ?? ""}</td>
                <td>{row.estado ?? ""}</td>
                <td>{formatDate(row.fechaReporte)}</td>
                <td>
                  <button
                    className="btn-ghost"
                    data-testid={`hotlist-delete-${row.id}`}
                    onClick={async () => {
                      if (!confirm("¿Eliminar entrada?")) return;
                      await deleteHotlist(row.id);
                      await qc.invalidateQueries({ queryKey: ["hotlist"] });
                    }}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <footer className="flex items-center justify-between text-xs text-muted-200">
        <span>{data?.total ?? 0} entradas</span>
        <div className="flex gap-1">
          <button
            disabled={page <= 1}
            className="btn-ghost"
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="px-3 py-2">Página {page}</span>
          <button
            disabled={(data?.items?.length ?? 0) < 50}
            className="btn-ghost"
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      </footer>
    </div>
  );
}
